import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Typography } from '../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../../../translations';
import { calculateIndexValue, calculateTrend } from '../../../../utils/budgetPlanned';
import { LWLYBadgeContainer, LWLYBadgeText } from './ComparisonBadges.styles';

export type Comparison = {
  current: number;
  isEffectiveHours: boolean;
  lastWeek?: number;
  lastWeekFormatted?: string;
  lastYear?: number;
  lastYearFormatted?: string;
};

type ComparisonBadgesProps = {
  comparison: Comparison;
};

export const ComparisonBadges: FC<ComparisonBadgesProps> = ({ comparison }) => {
  const { t } = useTranslation();
  const { current, lastWeek, lastWeekFormatted, lastYear, lastYearFormatted, isEffectiveHours } = comparison;
  const { common } = homeTranslations;

  const renderBadge = (label: string, fullLabel: string, compared: number, formattedValue?: string) => {
    if (!compared) return null;
    const indexValue = calculateIndexValue(current, compared);
    const indexStatus = calculateTrend(current, compared, isEffectiveHours);
    const tooltipContent = formattedValue ? (
      <ColumnFlexContainer padding={[1]} gap={[1]}>
        <Typography type="caption" color="white">
          {fullLabel}:
        </Typography>
        <Typography type="caption" color="white">
          {formattedValue} ({indexValue})
        </Typography>
      </ColumnFlexContainer>
    ) : undefined;

    return (
      <LWLYBadgeContainer key={label} status={indexStatus} padding={[1]} gap={[1]} borderRadius={[1]}>
        <Tooltip
          text={
            tooltipContent ?? (
              <Typography padding={[1]} color="white">
                {label}
              </Typography>
            )
          }
          position="bottom"
          isVisible={!!tooltipContent}
        >
          <div>
            <LWLYBadgeText type="legal" status={indexStatus}>
              {label}
            </LWLYBadgeText>
          </div>
        </Tooltip>
      </LWLYBadgeContainer>
    );
  };

  const lastWeekFullLabel = t(common.lastWeek.key, common.lastWeek.defaultValue);
  const lastYearFullLabel = t(common.lastYear.key, common.lastYear.defaultValue);
  const lastWeekLabel = lastWeekFullLabel
    .split(' ')
    .map((w) => w[0])
    .join('');
  const lastYearLabel = lastYearFullLabel
    .split(' ')
    .map((w) => w[0])
    .join('');

  const badges = [
    lastWeek !== undefined ? renderBadge(lastWeekLabel, lastWeekFullLabel, lastWeek, lastWeekFormatted) : null,
    lastYear !== undefined ? renderBadge(lastYearLabel, lastYearFullLabel, lastYear, lastYearFormatted) : null,
  ].filter(Boolean);

  if (!badges.length) return null;

  return (
    <RowFlexContainer gap={[1]} flexWrap="wrap">
      {badges}
    </RowFlexContainer>
  );
};
